import {
  type CSSProperties,
  cloneElement,
  forwardRef,
  type ReactElement
} from 'react';
import styled from 'styled-components';
import type {
  PositionValue,
  PositionWithOffsetValue
} from '@elfsight/configurator-contracts';
import { Portal } from '@elfsight/widget-modal';
import type { ButtonProps } from '../button';
import { Button } from '../button';
import type { AnimationFrameType } from './components';
import { AnimationFrame } from './components';
import { floatingButtonDefaults } from './defaultSettings';
import {
  buttonPositionMixin,
  buttonTransform,
  computeBorderRadius,
  getCurrentPosition
} from './utils';

const SHADOW = '0 4px 12px 0 rgba(0, 0, 0, 0.3)';

export type FloatingButtonProps = Omit<ButtonProps, 'slot'> & {
  position: PositionWithOffsetValue;
  verticalOffsetOnMobile?: number;
  horizontalOffsetOnMobile?: number;
  withShadow?: boolean;
  isMobile?: boolean;
  portalContainerElement?: Element;
  slot?: ReactElement;
  animationEnabled?: boolean;
  triggerAnimation?: AnimationFrameType['triggerAnimation'];
  animationInterval?: AnimationFrameType['animationInterval'];
  containerStyle?: CSSProperties;
  shadow?: boolean;
};

export const FloatingButton = forwardRef<HTMLDivElement, FloatingButtonProps>(
  (
    {
      position = floatingButtonDefaults.floatingButtonPosition,
      mode = floatingButtonDefaults.floatingButtonMode,
      color = floatingButtonDefaults.floatingButtonColor,
      verticalOffsetOnMobile = 20,
      horizontalOffsetOnMobile = 20,
      borderRadius:
        derivedBorderRadius = floatingButtonDefaults.floatingButtonCornerRadius,
      withShadow = true,
      iconOnly = false,
      isMobile = false,
      portalContainerElement,
      font = floatingButtonDefaults.floatingButtonFontStyle,
      slot,
      animationEnabled,
      triggerAnimation,
      animationInterval,
      containerStyle,
      style,
      shadow,
      onClick,
      onMouseLeave,
      onMouseOver,
      onMouseDown,
      ...forwardingProps
    },
    forwardingRef
  ) => {
    const {
      position: positionValue,
      verticalOffset,
      horizontalOffset
    } = position;

    const currentPosition = getCurrentPosition(
      positionValue,
      iconOnly,
      isMobile
    );

    let [currentVerticalOffset, currentHorizontalOffset] = isMobile
      ? [verticalOffsetOnMobile, horizontalOffsetOnMobile]
      : [verticalOffset, horizontalOffset];

    if (currentPosition === 'right' || currentPosition === 'left') {
      currentVerticalOffset = 0;
    } else if (currentPosition === 'top' || currentPosition === 'bottom') {
      currentHorizontalOffset = 0;
    }

    const borderRadius = computeBorderRadius(
      derivedBorderRadius,
      currentPosition,
      currentVerticalOffset,
      currentHorizontalOffset,
      iconOnly
    );

    return (
      <Portal containerElement={portalContainerElement} shadow={shadow}>
        <FloatingButtonContainer
          ref={forwardingRef}
          _position={currentPosition}
          _verticalOffset={currentVerticalOffset}
          _horizontalOffset={currentHorizontalOffset}
          _iconOnly={iconOnly}
          style={{
            ...containerStyle,
            margin: `${currentVerticalOffset}px ${currentHorizontalOffset}px`
          }}
        >
          {animationEnabled && (
            <AnimationFrame
              triggerAnimation={triggerAnimation}
              animationInterval={animationInterval}
              color={color}
              borderRadius={borderRadius}
            />
          )}

          <Button
            {...forwardingProps}
            mode={mode}
            color={color}
            iconOnly={iconOnly}
            borderRadius={borderRadius}
            font={font}
            onClick={onClick}
            onMouseLeave={onMouseLeave}
            onMouseOver={onMouseOver}
            onMouseDown={onMouseDown}
            style={{
              ...style,
              ...(withShadow ? { boxShadow: SHADOW } : {})
            }}
          />

          {slot &&
            cloneElement(slot, {
              onClick,
              onMouseLeave,
              onMouseOver,
              onMouseDown
            })}
        </FloatingButtonContainer>
      </Portal>
    );
  }
);

export interface FloatingButtonContainerProps {
  _position: PositionValue;
  _verticalOffset: number;
  _horizontalOffset: number;
  _iconOnly?: boolean;
}

const FloatingButtonContainer = styled.div<FloatingButtonContainerProps>`
  position: fixed;
  z-index: 99999999;

  ${buttonPositionMixin}
  ${buttonTransform}
`;
